// Minimal i18n: a flat message catalog per language, chosen once at module load.
// t(key, params) does {name} interpolation; tp(key, n, params) selects a plural
// form. Switching language persists the choice and reloads (see setLang).
import { en } from "./en.ts";
import { sl } from "./sl.ts";

export type Lang = "en" | "sl";
export const LANGS: Lang[] = ["en", "sl"];

const STORAGE_KEY = "chess:lang";
export const LANG_META: Record<Lang, { label: string; short: string }> = {
  en: { label: "English", short: "EN" },
  sl: { label: "Slovenščina", short: "SL" },
};

type Plural = { one: string; two?: string; few?: string; other: string };
type Entry = string | Plural;
type Catalog = Record<string, Entry>;
const CATALOGS: Record<Lang, Catalog> = { en, sl };

/** Detect: stored choice → first browser language that is sl/en → en. Guarded so
 *  importing this module in a non-browser context (the node unit test bundles
 *  modules that use t()) can't throw on missing localStorage/navigator. */
function detect(): Lang {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (s === "en" || s === "sl") return s;
  } catch { /* storage off */ }
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  if (!nav) return "en";
  const cands = (nav.languages && nav.languages.length
    ? nav.languages : [nav.language || "en"]);
  for (const c of cands) {
    const base = c.toLowerCase().split("-")[0];
    if (base === "sl") return "sl";
    if (base === "en") return "en";
  }
  return "en";
}

// Resolved at module load (detect() is a hoisted fn), so even a t() call made at
// another module's top level — before main.ts runs initLang() — reads the right
// language. initLang() re-detects + stamps <html lang>; both are idempotent.
let lang: Lang = detect();

export function getLang(): Lang { return lang; }

/** BCP-47 tag for Intl (dates, etc.). */
export function localeTag(): string { return lang === "sl" ? "sl-SI" : "en-GB"; }

/** Resolve + stamp <html lang>. Call once at boot, before startRouter(). */
export function initLang(): void {
  lang = detect();
  document.documentElement.setAttribute("lang", lang);
}

/** Test-only: switch the active language in-process. Production goes through
 *  setLang(), which persists the choice and reloads the app. */
export function __setLangForTest(next: Lang): void {
  lang = next;
}

/** Persist and reload so the whole app re-renders in the new language. */
export function setLang(next: Lang): void {
  if (next === lang) return;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* non-fatal */ }
  location.reload();
}

function lookup(key: string): Entry | undefined {
  return CATALOGS[lang][key] ?? CATALOGS.en[key];  // fall back to EN, then key
}

function fill(s: string, params?: Record<string, string | number>): string {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) =>
    k in params ? String(params[k]) : `{${k}}`);
}

/** Translate a plain key. NOTE: does NOT HTML-escape params — callers that
 *  interpolate into innerHTML must esc() any untrusted value first (same rule
 *  the current code already follows). */
export function t(key: string, params?: Record<string, string | number>): string {
  const e = lookup(key);
  if (e == null) { if (import.meta.env?.DEV) console.warn(`[i18n] missing key: ${key}`); return key; }
  if (typeof e === "string") return fill(e, params);
  return fill(e.other, params); // a plural entry used without a count
}

/** Slovenian CLDR cardinal category for integer n (v=0). English: one|other. */
function plural(n: number): "one" | "two" | "few" | "other" {
  const i = Math.abs(Math.trunc(n));
  if (lang === "sl") {
    const m = i % 100;
    if (m === 1) return "one";
    if (m === 2) return "two";
    if (m === 3 || m === 4) return "few";
    return "other";
  }
  return i === 1 ? "one" : "other";
}

/** Translate a count-dependent key; {n} is auto-added to params. */
export function tp(key: string, n: number, params?: Record<string, string | number>): string {
  const e = lookup(key);
  if (e == null || typeof e === "string") { if (import.meta.env?.DEV) console.warn(`[i18n] not a plural: ${key}`); return t(key, { n, ...params }); }
  const cat = plural(n);
  const form = e[cat] ?? e.other;
  return fill(form, { n, ...params });
}
